import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

export type FreezeTab = 'setup' | 'qc' | 'grading' | 'validation' | 'metrics';

type FreezeViewState = {
  activeTab: FreezeTab;
};

type FreezeViewAction = { type: 'SET_TAB'; tab: FreezeTab };

function viewReducer(
  state: FreezeViewState,
  action: FreezeViewAction,
): FreezeViewState {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, activeTab: action.tab };
    default:
      return state;
  }
}

const initialState: FreezeViewState = {
  activeTab: 'setup',
};

type FreezeViewContextValue = FreezeViewState & {
  setTab: (tab: FreezeTab) => void;
};

const FreezeViewContext = createContext<FreezeViewContextValue | null>(null);

export function FreezeViewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(viewReducer, initialState);

  const setTab = useCallback(
    (tab: FreezeTab) => dispatch({ type: 'SET_TAB', tab }),
    [],
  );

  const value = useMemo(() => ({ ...state, setTab }), [state, setTab]);

  return (
    <FreezeViewContext.Provider value={value}>
      {children}
    </FreezeViewContext.Provider>
  );
}

export function useFreezeView() {
  const ctx = useContext(FreezeViewContext);
  if (!ctx) {
    throw new Error('useFreezeView must be used within FreezeViewProvider');
  }
  return ctx;
}
